import { useCallback } from 'react'
import { useQuery } from '@maui/react-query'
import { useMatiApiClient } from './client'
import { actorHistoryAdapter, formatCampaignChart } from '@/adapters'

export const useActorQuery = (id, options = {}) => {
  const api = useMatiApiClient('actors')
  return useQuery({
    queryFn: () => api.fetch(id),
    queryKey: ['actors', id],
    refetchOnWindowFocus: false,
    ...options
  })
}

const actorCampaignsAdapters = [
  ['default', formatCampaignChart],
  ['__raw', data => data]
]

export const actorCampaignsQueryFn = async ({ id, api }) => {
  const data = await api.fetchActorCampaigns(id)
  const adaptedData = await Promise.all(actorCampaignsAdapters.map(([key, adapter]) => {
    return adapter(data)
  }))
  return adaptedData.reduce((acc, curr, i) => {
    acc[actorCampaignsAdapters[i][0]] = curr
    return acc
  }, {})
}

export const useActorCampaignsQuery = (id, options = {}) => {
  if (!id) throw new Error('useActorCampaignsQuery requires an id')
  const api = useMatiApiClient('actors')
  const { adapter = false, ...restOptions } = options
  const adapterFn = actorCampaignsAdapters.find(([key]) => key === adapter || (adapter === true && key === 'default') || (adapter === false && key === '__raw'))
  return useQuery({
    queryFn: () => actorCampaignsQueryFn({ id, api }),
    queryKey: ['actorsCampaign', adapter, id],
    select: useCallback(data => data[adapterFn[0]] || data.__raw, [adapterFn]),
    ...restOptions,
  })
}

const adapters = [
  ['default', actorHistoryAdapter],
  ['__raw', data => data]
]

export const actorHistoryQueryFn = async ({ id, api }) => {
  const data = await api.fetchActorHistory(id)
  const adaptedData = await Promise.all(adapters.map(([key, adapter]) => {
    return adapter(data)
  }))
  return adaptedData.reduce((acc, curr, i) => {
    acc[adapters[i][0]] = curr
    return acc
  }, {})
}

export const useActorHistoryQuery = (id, options = {}) => {
  if (!id) throw new Error('useActorHistoryQuery requires an id')
  const api = useMatiApiClient('actors')
  const { adapter = false, ...restOptions } = options
  const adapterFn = adapters.find(([key]) => key === adapter || (adapter === true && key === 'default') || (adapter === false && key === '__raw'))
  return useQuery({
    queryFn: () => actorHistoryQueryFn({ id, api }),
    queryKey: ['actorsHistory', adapter, id],
    refetchOnWindowFocus: false,
    select: useCallback(data => data[adapterFn[0]] || data.__raw, [adapterFn]),
    ...restOptions,
    enabled: (restOptions.enabled ?? true) && !!id,
  })
}

export const useActorsMalwareQuery = (params, options = {}) => {
  if (!params) throw new Error('useActorsMalwareQuery requires threat_actors')
  const api = useMatiApiClient('actors')
  return useQuery({
    queryFn: () => api.fetchActorsMalware(params),
    queryKey: ['actorsMalware', params],
    ...options
  })
}

export const useActorMttQuery = (params, options = {}) => {
  if (!params) throw new Error('useActorMttQuery requires filters')
  const api = useMatiApiClient('actors')
  return useQuery({
    queryFn: () => api.fetchActorMtt(params),
    queryKey: ['actorsMtt', params],
    ...options
  })
}

export const useFetchActorVocab = (options = {}) => {
  const api = useMatiApiClient('actors')
  return useQuery({
    queryFn: () => api.fetchActorVocab(),
    queryKey: ['actorsVocab'],
    ...options
  })
}

export const useActorTtpQuery = (params, options = {}) => {
  if (!params) throw new Error('useActorTtpQuery requires threat_actors')
  const api = useMatiApiClient('actors')
  return useQuery({
    queryFn: () => api.fetchActorTtp(params),
    queryKey: ['actorsTtp', params],
    ...options
  })
}
